import { mergeClasses, tokens, makeStyles } from "@fluentui/react-components";
import { useLanguage } from "../i18n/LanguageContext";

const useNavStyles = makeStyles({
  wrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    maxWidth: "720px",
    margin: "0 auto 16px",
    flexWrap: "wrap",
  },
  bar: {
    display: "flex",
    gap: "4px",
    padding: "4px",
    borderRadius: "10px",
    backgroundColor: tokens.colorNeutralBackground3,
    flex: 1,
  },
  langBar: {
    display: "flex",
    gap: "2px",
    padding: "3px",
    borderRadius: "10px",
    backgroundColor: tokens.colorNeutralBackground3,
  },
  langItem: {
    padding: "6px 9px",
    borderRadius: "7px",
    fontSize: "12px",
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    border: "none",
    backgroundColor: "transparent",
    transitionProperty: "background-color, color",
    transitionDuration: "120ms",
    ":hover": {
      color: tokens.colorNeutralForeground1,
    },
  },
  langItemActive: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorBrandForeground1,
    boxShadow: tokens.shadow2,
  },
  item: {
    flex: 1,
    textAlign: "center",
    padding: "7px 10px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    border: "none",
    backgroundColor: "transparent",
    transitionProperty: "background-color, color",
    transitionDuration: "120ms",
    ":hover": {
      color: tokens.colorNeutralForeground1,
    },
  },
  itemActive: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorBrandForeground1,
    boxShadow: tokens.shadow2,
  },
});

export type NavRoute = "home" | "guide" | "settings" | "users" | "contact";

interface AppNavProps {
  route: NavRoute;
  showUsers: boolean;
}

function navigate(route: NavRoute) {
  window.location.hash = route === "home" ? "" : `#/${route}`;
}

const UI_LANGUAGES = ["en", "tr"] as const;

/** Minimal in-app menu with a permanent user guide and an EN/TR language switcher. */
export function AppNav({ route, showUsers }: AppNavProps) {
  const s = useNavStyles();
  const { language, setLanguage, t } = useLanguage();
  const items: { key: NavRoute; label: string }[] = [
    { key: "home", label: t("nav.home") },
    { key: "guide", label: t("nav.guide") },
    { key: "contact", label: t("nav.contact") },
    { key: "settings", label: t("nav.settings") },
    ...(showUsers ? [{ key: "users" as NavRoute, label: t("nav.users") }] : []),
  ];

  return (
    <div className={s.wrap}>
      <nav className={s.bar}>
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            className={mergeClasses(s.item, route === item.key && s.itemActive)}
            onClick={() => navigate(item.key)}
            aria-current={route === item.key ? "page" : undefined}
          >
            {item.label}
          </button>
        ))}
      </nav>
      <div className={s.langBar} role="group" aria-label="Interface language">
        {UI_LANGUAGES.map((lang) => (
          <button
            key={lang}
            type="button"
            className={mergeClasses(s.langItem, language === lang && s.langItemActive)}
            onClick={() => setLanguage(lang)}
            aria-pressed={language === lang}
          >
            {lang.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
}
